import React from "react";
import { motion } from "framer-motion";
import {
  Camera,
  Upload,
  X,
  Plus,
  FileText,
  Info,
} from "lucide-react";
import favicon from "../assets/favicon_new.png";

// Checkbox component (extracted from Dashboard.js)
const Checkbox = ({ id, checked, onChange, isDarkMode }) => (
  <input
    id={id}
    type="checkbox"
    checked={checked}
    onChange={onChange}
    className={`form-checkbox h-5 w-5 ${
      isDarkMode
        ? "text-blue-500 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-offset-gray-800"
        : "text-blue-500 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-offset-white"
    }`}
  />
);

const ScanSection = ({
  // Props for state and data
  templates,
  templatesLoading,
  templatesError,
  selectedTemplate,
  setSelectedTemplate,
  batchProcess,
  setBatchProcess,
  splitPdf,
  setSplitPdf,
  withInventoryLedger,
  withoutInventoryLedger,
  companies,
  selectedCompany,
  setSelectedCompany,
  capturedImages,
  setCapturedImages,
  loading,
  isDarkMode,
  isMobile,
  
  // Props for handlers
  setActiveSection,
  setShowCreateTemplate,
  handleCapture,
  handleFileUpload,
  submitImages,
  removeImage,
  handleSubmit,
  handleRemoveImage,
  handleWithInventoryLedgerChange,
  handleWithoutInventoryLedgerChange,
  hasFeature,
  hasCompanyManagement,
}) => {
  // Support both legacy and new prop names
  const onSubmitFiles = submitImages || handleSubmit;
  const onRemoveImage = removeImage || handleRemoveImage;
  
  // Handle file submission with notification
  const handleFileSubmission = () => {
    console.log("handleFileSubmission called");
    console.log("onSubmitFiles exists:", !!onSubmitFiles);
    console.log("capturedImages length:", capturedImages?.length);
    console.log("selectedTemplate:", selectedTemplate);
    console.log("selectedCompany:", selectedCompany);
    console.log("hasCompanyManagement:", hasCompanyManagement);
    
    // Check if there are files to process
    if (!capturedImages || capturedImages.length === 0) {
      console.log("No files to process, notification not shown");
      if (onSubmitFiles) {
        onSubmitFiles();
      }
      return;
    }
    
    // Check if template is selected (always required)
    if (!selectedTemplate) {
      console.log("No template selected, notification not shown");
      if (onSubmitFiles) {
        onSubmitFiles();
      }
      return;
    }
    
    // Check if company is selected (only required if company management is enabled)
    if (hasCompanyManagement && !selectedCompany) {
      console.log("Company management enabled but no company selected, notification not shown");
      if (onSubmitFiles) {
        onSubmitFiles();
      }
      return;
    }
    
    if (onSubmitFiles) {
      console.log("Calling onSubmitFiles");
      onSubmitFiles();
      // Note: Notification will be shown after successful upload in Dashboard component
    } else {
      console.log("onSubmitFiles is undefined");
    }
  };
  

  
  return (
    <div className="space-y-8 animate-in fade-in duration-500 overflow-hidden">
      {templates.length === 0 ? (
        // Enhanced Template prompt popup
        <div>
          <div className="absolute inset-0"></div>
          <div className="relative p-10 flex flex-col items-center text-center space-y-6">
            {/* Enhanced Icon Container */}
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-blue-500 shadow-lg flex items-center justify-center transform hover:scale-105 transition-transform duration-300">
                <FileText className="w-10 h-10 text-white" />
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="space-y-4 max-w-lg">
              <h3 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Create Your First Template
              </h3>
              <p className="text-slate-500 text-lg leading-relaxed">
                Upload a sample document and select the fields you want
                to extract. This template will streamline processing for
                similar documents.
              </p>
            </div>

            {/* Enhanced CTA Button */}
            <button
              onClick={() => {
                setActiveSection("templates");
                setShowCreateTemplate(true);
              }}
              className="group relative px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 ease-out"
            >
              <div className="flex items-center space-x-3">
                <Plus
                  size={20}
                  className="transition-transform duration-300"
                />
                <span>Create Template</span>
              </div>
            </button>
          </div>
        </div>
      ) : (
        // Enhanced existing scan section content
        <div className="space-y-8 p-2">
          {/* Enhanced Action Buttons */}
          <div
            className={`${
              isMobile ? "" : "hidden"
            } flex items-center justify-center`}
          >
            <div className="justify-center sm:grid-cols-2 gap-4 max-w-md mx-auto">
              <button
                onClick={handleCapture}
                className={`group justify-center relative overflow-hidden  ${
                  isDarkMode
                    ? "bg-gray-800/95 text-white border-slate-950"
                    : "bg-white text-slate-800 border-slate-280"
                } border hover:border-blue-300 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                  <span className="font-semibold transition-colors duration-300">
                    Scan Document
                  </span>
                </div>
              </button>
            </div>
          </div>

          {/* Enhanced Divider */}
          <div
            className={`${
              isMobile ? "" : "hidden"
            } relative flex items-center justify-center py-4`}
          >
            <span
              className={`${
                isDarkMode ? "text-white" : "text-slate-500"
              } font-medium text-sm tracking-wider uppercase`}
            >
              Or
            </span>
          </div>

          {/* Enhanced Upload Zone - Responsive */}
          <div className=" px-3 sm:px-4 lg:px-6 max-w-2xl mx-auto">
            <label
              htmlFor="dropzone-file"
              className={`group relative flex flex-col items-center justify-center w-full 
  h-28 sm:h-32 lg:h-36 
  border-2 border-dashed rounded-lg sm:rounded-xl 
  cursor-pointer transition-all duration-300 ${
    isDarkMode
      ? "bg-gray-800/95 border-white/30 hover:bg-gray-700/95"
      : "bg-white border-slate-280 hover:bg-slate-50"
  } hover:border-blue-400 hover:shadow-md`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.add(
                  "border-blue-500",
                  isDarkMode ? "bg-blue-900/20" : "bg-blue-50"
                );
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove(
                  "border-blue-500",
                  isDarkMode ? "bg-blue-900/20" : "bg-blue-50"
                );
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove(
                  "border-blue-500",
                  isDarkMode ? "bg-blue-900/20" : "bg-blue-50"
                );

                const files = Array.from(e.dataTransfer.files);
                files.forEach((file) => {
                  const fileNameLower = (file.name || "").toLowerCase();
                  const isPdf = file.type === "application/pdf" || fileNameLower.endsWith(".pdf");
                  const isSpreadsheet =
                    file.type === "text/csv" ||
                    file.type === "application/vnd.ms-excel" ||
                    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                    fileNameLower.endsWith(".csv") ||
                    fileNameLower.endsWith(".xls") ||
                    fileNameLower.endsWith(".xlsx");

                  setCapturedImages((prev) => [
                    ...prev,
                    {
                      file,
                      displayImage: !isPdf && !isSpreadsheet ? URL.createObjectURL(file) : undefined,
                      filename: file.name,
                      isPdf,
                      isSpreadsheet,
                    },
                  ]);
                });
              }}
            >
              <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-3 p-3 sm:p-4 lg:p-5">
                {/* Upload Icon */}
                <div
                  className={`
    w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12
    rounded-lg sm:rounded-xl 
    ${
      isDarkMode
        ? "bg-gradient-to-br from-gray-700 to-gray-600 group-hover:from-blue-600 group-hover:to-indigo-600"
        : "bg-gradient-to-br from-slate-100 to-slate-200 group-hover:from-blue-100 group-hover:to-indigo-100"
    }
    flex items-center justify-center 
    group-hover:scale-110 transition-all duration-300
  `}
                >
                  <Upload className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-slate-500 group-hover:text-blue-600 transition-colors duration-300" />
                </div>

                {/* Upload Text */}
                <div className="text-center space-y-1 sm:space-y-2">
                  <p
                    className={`
      text-xs sm:text-sm lg:text-base font-semibold 
      transition-colors duration-300 ${
        isDarkMode
          ? "text-white group-hover:text-blue-400"
          : "text-slate-800 group-hover:text-blue-600"
      }
    `}
                  >
                    <span className="font-bold">Click to upload</span>
                    <span className="hidden sm:inline">
                      {" "}
                      or drag and drop
                    </span>
                  </p>
                  <p
                    className={`
      text-xs sm:text-sm lg:text-base ${
        isDarkMode ? "text-gray-300" : "text-slate-600"
      }
    `}
                  >
                    <span className="block sm:hidden">
                      PDF, Images, Excel
                    </span>
                    <span className="hidden sm:block lg:hidden">
                      PDF, PNG, JPG, CSV, Excel files
                    </span>
                    <span className="hidden lg:block">
                      PDF, PNG, JPG, JPEG, CSV, XLS, XLSX (MAX. 5MB)
                    </span>
                  </p>
                </div>
              </div>

              <input
                id="dropzone-file"
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept="image/*,.pdf,.csv,.xls,.xlsx"
                multiple
              />
            </label>
          </div>

          {/* Enhanced Options */}
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Processing Options */}
            <div
              className={`${
                isDarkMode
                  ? "bg-gray-800/95 border-slate-950"
                  : "bg-white border-slate-280"
              } rounded-xl border  p-6 shadow-sm`}
            >
              <h4
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-slate-800"
                } mb-4`}
              >
                Processing Options
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label
                  htmlFor="batchProcess"
                  className={`flex items-start space-x-3 p-3 rounded-2xl cursor-pointer ${
                    isDarkMode ? "" : "border-slate-280"
                  } transition-all duration-300 border border-transparent hover:border-blue-400 hover:text-blue-600 group`}
                >
                  <Checkbox
                    id="batchProcess"
                    checked={batchProcess}
                    onChange={(e) => setBatchProcess(e.target.checked)}
                    isDarkMode={isDarkMode}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-white" : "text-slate-800"
                      } `}
                    >
                      Batch Processing
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-white" : "text-slate-600"
                        }`}
                      >
                        Process files individually
                      </p>
                      <div className="group/tooltip relative">
                        <Info
                          size={14}
                          className="text-blue-500 hover:text-blue-600 cursor-help"
                          onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle when clicking info icon
                        />
                        <div
                          className={`absolute left-0 bottom-full mb-2 w-56 p-3 ${
                            isDarkMode
                              ? "bg-slate-800 text-white"
                              : "bg-white text-slate-800"
                          } border text-xs rounded-lg shadow-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 z-10 pointer-events-none`}
                        >
                          Process each file individually as a separate
                          document, rather than combining all files into
                          one document.
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
                <label
                  htmlFor="splitPdf"
                  className={`flex items-start space-x-3 p-3 rounded-2xl ${
                    isDarkMode ? "" : "border-slate-280"
                  } transition-all duration-300 border border-transparent hover:border-blue-400 cursor-pointer`}
                >
                  <Checkbox
                    id="splitPdf"
                    checked={splitPdf}
                    onChange={(e) => setSplitPdf(e.target.checked)}
                    isDarkMode={isDarkMode}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-white" : "text-slate-800"
                      }`}
                    >
                      Split PDF Pages
                    </span>
                    <div className="flex items-center space-x-2 mt-1">
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-white" : "text-slate-600"
                        }`}
                      >
                        Process pages separately
                      </p>
                      <div className="group relative">
                        <Info
                          size={14}
                          className="text-blue-500 hover:text-blue-600 cursor-help"
                        />
                        <div
                          className={`absolute left-0 bottom-full mb-2 w-56 p-3 ${
                            isDarkMode
                              ? "bg-slate-800 text-white"
                              : "bg-white text-slate-800"
                          } text-xs rounded-lg border shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none`}
                        >
                          Split multi-page PDFs into individual pages
                          and process each page as a separate document.
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Tally Ledger Options - Enhanced */}
            {hasFeature("tally_integration") && (
              <div
                className={`rounded-2xl border p-6 transition-all duration-300 ${
                  isDarkMode
                    ? "bg-gray-800/95 border-slate-950"
                    : "bg-white border-slate-280"
                } `}
              >
                <h4
                  className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-slate-800"
                  } `}
                >
                  Tally Ledger Options
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="group flex items-center space-x-3 p-4 rounded-2xl hover:border-blue-400 border border-transparent transition-all duration-300">
                    <Checkbox
                      id="withInventoryLedger"
                      checked={withInventoryLedger}
                      onChange={(e) =>
                        handleWithInventoryLedgerChange(
                          e.target.checked
                        )
                      }
                      isDarkMode={isDarkMode}
                    />
                    <label
                      htmlFor="withInventoryLedger"
                      className={`text-sm font-semibold cursor-pointer transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-slate-800"
                      } `}
                    >
                      With Inventory Ledger
                    </label>
                  </div>
                  <div className="group flex items-center space-x-3 p-4 rounded-2xl hover:border-blue-400 border border-transparent transition-all duration-300">
                    <Checkbox
                      id="withoutInventoryLedger"
                      checked={withoutInventoryLedger}
                      onChange={(e) =>
                        handleWithoutInventoryLedgerChange(
                          e.target.checked
                        )
                      }
                      isDarkMode={isDarkMode}
                    />
                    <label
                      htmlFor="withoutInventoryLedger"
                      className={`text-sm font-semibold cursor-pointer transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-slate-800"
                      } `}
                    >
                      Without Inventory Ledger
                    </label>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-white" : "text-slate-600"
                    }`}
                  >
                    Select your preferred ledger configuration options
                  </p>
                </div>
              </div>
            )}

            {/* Enhanced Note Box with applied color scheme */}
            {hasFeature("tally_integration") && (
              <div
                className={`border-l-4 border-blue-400 border-t border-r border-b rounded-r-2xl p-4 transition-all duration-300 ${
                  isDarkMode
                    ? "bg-gray-800/95 border-t-white/30 border-r-white/30 border-b-white/30"
                    : "bg-white border-t-slate-280 border-r-slate-280 border-b-slate-280"
                } hover:border-blue-500`}
              >
                <div className="flex items-start space-x-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-300 ${
                      isDarkMode ? "bg-gray-700/80" : "bg-slate-100"
                    }`}
                  >
                    <svg
                      className="w-4 h-4 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p
                      className={`text-sm font-semibold transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-slate-800"
                      } hover:text-blue-600`}
                    >
                      Multi-page Invoice Processing
                    </p>
                    <p
                      className={`text-sm mt-1 ${
                        isDarkMode ? "text-white/80" : "text-slate-600"
                      }`}
                    >
                      For invoices with multiple pages, please upload
                      them as a single PDF file for optimal processing.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Dropdowns */}
            {hasFeature("company_management") && (
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="company"
                    className={`block text-sm font-semibold ${
                      isDarkMode ? "text-white" : "text-slate-700"
                    } mb-2`}
                  >
                    Select Company
                  </label>
                  <select
                    id="company"
                    value={selectedCompany?.id || ""}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setSelectedCompany(null);
                        return;
                      }
                      const company = companies.find(
                        (c) => String(c.id) === String(e.target.value)
                      );
                      if (company) {
                        setSelectedCompany(company);
                      }
                    }}
                    className={`w-full px-4 py-3 border rounded-xl ${
                      isDarkMode
                        ? "bg-gray-800/95 text-white border-slate-950"
                        : "bg-white border-slate-280 text-black"
                    }
                    `}
                    required
                  >
                    <option value="">Choose a company...</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div>
              <label
                className={`block text-sm font-semibold ${
                  isDarkMode ? "text-white" : "text-slate-700"
                } mb-4`}
              >
                Select Template
              </label>

              {templatesLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {[...Array(6)].map((_, index) => (
                    <div key={index} className="animate-pulse">
                      <div className="bg-slate-200 rounded-xl h-24 mb-2"></div>
                      <div className="bg-slate-200 rounded h-4"></div>
                    </div>
                  ))}
                </div>
              ) : templatesError ? (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-xl border border-red-200">
                  {templatesError}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  
                  {/* Custom Template Button with Light Green Gradient */}
                  <div
                    onClick={() => {
                      setActiveSection("templates");
                    }}
                    className={`cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200 hover:scale-105 ${
                      isDarkMode
                        ? "border-emerald-500/40 bg-gradient-to-br from-emerald-900/20 via-green-800/15 to-teal-900/20 hover:border-emerald-400 hover:from-emerald-800/30 hover:via-green-700/25 hover:to-teal-800/30"
                        : "border-emerald-300/60 bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 hover:border-emerald-400 hover:from-emerald-100 hover:via-green-100 hover:to-teal-100"
                    }`}
                  >
                    {/* Custom Template Icon */}
                    <div className="p-3 pb-2">
                      <div
                        className={`w-full h-20 rounded-lg flex items-center justify-center ${
                          isDarkMode
                            ? "bg-gradient-to-br from-emerald-800/30 via-green-700/25 to-teal-800/30"
                            : "bg-gradient-to-br from-emerald-100/80 via-green-100/60 to-teal-100/80"
                        }`}
                      >
                        <div className="text-center">
                          <svg
                            className={`w-10 h-10 mx-auto ${
                              isDarkMode
                                ? "text-emerald-300"
                                : "text-emerald-600"
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Custom Template Label */}
                    <div className="px-3 pb-3">
                      <p
                        className={`text-sm font-medium text-center ${
                          isDarkMode
                            ? "text-emerald-200"
                            : "text-emerald-700"
                        }`}
                      >
                        Custom Template
                      </p>
                    </div>
                  </div>

                  {/* Template Cards */}
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`relative cursor-pointer rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                        selectedTemplate?.id === template.id
                          ? isDarkMode
                            ? "border-blue-500 bg-blue-500/20"
                            : "border-blue-500 bg-blue-50"
                          : isDarkMode
                          ? "border-gray-600 bg-gray-800/50 hover:border-gray-500"
                          : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-md"
                      }`}
                    >
                      {/* Template Image */}
                      <div className={`p-3 pb-2 `}>
                        {/* Enhanced SVG Icon */}
                        <div className="flex justify-center">
                          <div className="relative">
                            {/* Corner brackets only - no inner border frame */}
                            {/* Top-left corner */}
                            <div
                              className={`absolute -top-2 -left-2 w-4 h-4 border-l-2 border-t-2 rounded-tl-md ${
                                isDarkMode
                                  ? "border-blue-400/50"
                                  : "border-blue-600/50"
                              }`}
                            ></div>
                            {/* Top-right curved border */}
                            <div
                              className={`absolute -top-2 -right-2 w-4 h-4 border-r-2 border-t-2 rounded-tr-md ${
                                isDarkMode
                                  ? "border-blue-400/50"
                                  : "border-blue-600/50"
                              }`}
                            ></div>
                            {/* Bottom-left curved border */}
                            <div
                              className={`absolute -bottom-2 -left-2 w-4 h-4 border-l-2 border-b-2 rounded-bl-md ${
                                isDarkMode
                                  ? "border-blue-400/50"
                                  : "border-blue-600/50"
                              }`}
                            ></div>
                            {/* Bottom-right corner */}
                            <div
                              className={`absolute -bottom-2 -right-2 w-4 h-4 border-r-2 border-b-2 rounded-br-md ${
                                isDarkMode
                                  ? "border-blue-400/50"
                                  : "border-blue-600/50"
                              }`}
                            ></div>

                            <svg
                              className={`w-20 h-20 ${
                                isDarkMode
                                  ? "text-blue-400"
                                  : "text-blue-600"
                              }`}
                              fill="none"
                              viewBox="0 0 32 32"
                            >
                              {/* Outer rounded rectangle (shadow/background layer) */}
                              <rect
                                x="6.2"
                                y="4.2"
                                width="18"
                                height="22"
                                rx="2.5"
                                ry="2.5"
                                className={`${
                                  isDarkMode
                                    ? "fill-gray-700/60"
                                    : "fill-gray-300/60"
                                }`}
                                strokeWidth="0"
                              />

                              {/* Main document body */}
                              <rect
                                x="6"
                                y="4"
                                width="18"
                                height="22"
                                rx="2.5"
                                ry="2.5"
                                className={`${
                                  isDarkMode
                                    ? "fill-gray-800 stroke-gray-600"
                                    : "fill-white stroke-gray-300"
                                }`}
                                strokeWidth="0.5"
                              />

                              {/* Folded corner triangle */}
                              <path
                                d="M20 4 L20 8 L24 8 Z"
                                className={`${
                                  isDarkMode
                                    ? "fill-gray-700"
                                    : "fill-gray-100"
                                }`}
                              />

                              {/* Corner fold line */}
                              <path
                                d="M20 4 L20 8 L24 8"
                                className={`${
                                  isDarkMode
                                    ? "stroke-gray-600"
                                    : "stroke-gray-300"
                                }`}
                                strokeWidth="0.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                              />

                              {/* Document content lines */}
                              <g
                                className={`${
                                  isDarkMode
                                    ? "stroke-gray-400"
                                    : "stroke-gray-500"
                                }`}
                                strokeWidth="0.8"
                                strokeLinecap="round"
                              >
                                {/* Header/title line */}
                                <line x1="9" y1="11" x2="17" y2="11" />

                                {/* Content lines with varying lengths */}
                                <line x1="9" y1="14" x2="21" y2="14" />
                                <line x1="9" y1="17" x2="20" y2="17" />
                                <line x1="9" y1="20" x2="18" y2="20" />
                                <line x1="9" y1="23" x2="15" y2="23" />
                              </g>
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Template Name */}
                      <div className="px-3 pb-3 mt-2">
                        <p
                          className={`text-sm font-medium text-center break-words ${
                            isDarkMode
                              ? "text-gray-200"
                              : "text-gray-800"
                          }`}
                        >
                          {template.name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Enhanced File Preview */}
          {capturedImages.length > 0 && (
            <div
              className={`${
                isDarkMode
                  ? "bg-gray-800/95 border-gray-700/50"
                  : "bg-white border-slate-200"
              } rounded-3xl border shadow-xl overflow-hidden animate-in slide-in-from-bottom-4 duration-500 backdrop-blur-sm`}
            >
              {/* Action Buttons Header */}
              <div
                className={`${
                  isDarkMode
                    ? "bg-gradient-to-r from-gray-900/95 to-gray-800/95 border-gray-700/50"
                    : "bg-gradient-to-r from-slate-50 to-blue-50/50 border-slate-200"
                } px-8 py-6 border-b`}
              >
                <div className="flex flex-col sm:flex-row gap-4 max-w-4xl">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      console.debug("Submit All Files clicked", {
                        capturedCount: capturedImages?.length,
                        selectedTemplateId: selectedTemplate?.id,
                      });
                      handleFileSubmission();
                    }}
                    disabled={loading}
                    className="z-10 flex-1 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 transition-all duration-300 ease-out min-h-[56px] flex items-center justify-center"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center gap-3">
                        <div className="relative w-7 h-7">
                          <motion.div
                            className="w-full h-full"
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 2,
                              ease: "linear",
                              repeat: Infinity,
                            }}
                          >
                            <img
                              src={favicon}
                              alt="Loading"
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                console.log(
                                  "Image failed to load:",
                                  e.target.src
                                );
                                e.target.style.display = "none";
                              }}
                            />
                          </motion.div>
                        </div>
                        <span>Processing...</span>
                      </div>
                    ) : (
                      <span className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                          />
                        </svg>
                        Submit All Files
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setCapturedImages([])}
                    className={`flex-1 px-8 py-4 ${
                      isDarkMode
                        ? "bg-gray-700/50 border-red-400/30 text-red-400 hover:bg-red-900/20 hover:border-red-400/50"
                        : "bg-white border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                    } border-2 font-semibold rounded-2xl transform hover:-translate-y-0.5 transition-all duration-300 min-h-[56px] flex items-center justify-center gap-2`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Clear All
                  </button>
                </div>
              </div>

              {/* File Grid Section */}
              <div
                className={`${
                  isDarkMode ? "bg-gray-800/50" : "bg-slate-50/30"
                } px-8 py-8`}
              >
                {/* Section Header */}
                <div className="flex items-center justify-between mb-8">
                  <h3
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-white" : "text-slate-800"
                    } flex items-center gap-4`}
                  >
                    <span>Selected Files</span>
                    <div className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl text-sm font-bold shadow-lg">
                      {capturedImages.length}
                    </div>
                  </h3>
                </div>

                {/* File Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl">
                  {capturedImages.map((item, index) => (
                    <div
                      key={index}
                      className={`group relative rounded-2xl overflow-hidden ${
                        isDarkMode
                          ? "bg-gray-700/50 border-gray-600/30 hover:bg-gray-700/70"
                          : "bg-white border-slate-200 hover:bg-slate-50"
                      } border hover:border-blue-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-[1.02]`}
                    >
                      {item.isPdf ? (
                        <div className="p-6">
                          <div className="flex flex-col items-center space-y-4">
                            <div
                              className={`w-16 h-16 ${
                                isDarkMode
                                  ? "bg-red-500/20 text-red-400"
                                  : "bg-red-100 text-red-600"
                              } rounded-2xl flex items-center justify-center`}
                            >
                              <svg
                                className="w-8 h-8"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                            <div className="text-center">
                              <span
                                className={`text-sm font-semibold block mb-1 ${
                                  isDarkMode
                                    ? "text-gray-200"
                                    : "text-slate-700"
                                }`}
                              >
                                PDF Document
                              </span>
                              <span
                                className={`text-xs truncate block max-w-[160px] ${
                                  isDarkMode
                                    ? "text-gray-400"
                                    : "text-slate-500"
                                }`}
                                title={item.filename}
                              >
                                {item.filename}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : item.isCsv || item.isSpreadsheet ? (
                        <div className="p-6">
                          <div className="flex flex-col items-center space-y-4">
                            <div
                              className={`w-16 h-16 ${
                                isDarkMode
                                  ? "bg-emerald-500/20 text-emerald-400"
                                  : "bg-emerald-100 text-emerald-600"
                              } rounded-2xl flex items-center justify-center relative`}
                            >
                              {/* CSV Icon - Table with document */}
                              <svg
                                className="w-8 h-8"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                {/* Document outline */}
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                                {/* Table lines inside */}
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M9 8h6"
                                  opacity="0.7"
                                />
                              </svg>
                            </div>
                            <div className="text-center">
                              <span
                                className={`text-sm font-semibold block mb-1 ${
                                  isDarkMode
                                    ? "text-gray-200"
                                    : "text-slate-700"
                                }`}
                              >
                                {item.isCsv ? "CSV File" : "Spreadsheet"}
                              </span>
                              <span
                                className={`text-xs truncate block max-w-[160px] ${
                                  isDarkMode
                                    ? "text-gray-400"
                                    : "text-slate-500"
                                }`}
                                title={item.filename}
                              >
                                {item.filename}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="relative overflow-hidden">
                            <img
                              src={item.displayImage}
                              alt={`Document ${index + 1}`}
                              className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                          <div className="px-4 pb-4">
                            <div
                              className={`text-sm font-medium ${
                                isDarkMode
                                  ? "text-gray-200"
                                  : "text-slate-700"
                              } mb-1`}
                            >
                              Image File
                            </div>
                            <span
                              className={`text-xs truncate block ${
                                isDarkMode
                                  ? "text-gray-400"
                                  : "text-slate-500"
                              }`}
                              title={item.filename}
                            >
                              {item.filename}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Remove Button */}
                      <button
                        type="button"
                        aria-label="Remove file"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.debug("Remove file clicked", { index, filename: item?.filename });
                          onRemoveImage && onRemoveImage(index);
                        }}
                        className={`z-10 absolute top-3 right-3 w-9 h-9 ${
                          isDarkMode
                            ? "bg-red-500/90 hover:bg-red-600"
                            : "bg-red-500 hover:bg-red-600"
                        } text-white rounded-xl flex items-center justify-center ${
                          isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                        } transform scale-75 group-hover:scale-100 transition-all duration-300 shadow-lg hover:shadow-xl`}
                      >
                        <X size={18} />
                      </button>

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScanSection;

